// Per-group sharing controls for accountability groups (Phase 1).
//
// ShareItemControl: a "share with group(s)" multi-select for a prayer or grow
// goal, backed by ShareGrant rows. ShareSnapshotControl: a per-group toggle for
// sharing your latest finalized snapshot (backed by the membership flag).

import { useEffect, useState } from 'react';
import { Users, Loader2, Check } from 'lucide-react';
import { accountabilityGroupsAPI } from '../api/client';
import type { AccountabilityGroup } from '../types';
import toast from 'react-hot-toast';

function useMyGroups() {
  const [groups, setGroups] = useState<AccountabilityGroup[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    accountabilityGroupsAPI
      .list()
      .then((g) => active && setGroups(g))
      .catch(() => active && setGroups([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);
  return { groups, setGroups, loading };
}

interface ShareGroupPickerProps {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  title?: string;
  emptyMessage?: string;
}

export function ShareGroupPicker({
  selectedIds,
  onChange,
  title = 'Share with accountability group(s)',
  emptyMessage = 'Join an accountability group to share this.',
}: ShareGroupPickerProps) {
  const { groups, loading } = useMyGroups();

  const toggle = (groupId: number) => {
    onChange(
      selectedIds.includes(groupId)
        ? selectedIds.filter((id) => id !== groupId)
        : [...selectedIds, groupId],
    );
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <Loader2 size={14} className="animate-spin" /> Loading groups…
      </div>
    );
  }
  if (groups.length === 0) {
    return <p className="text-xs text-gray-400">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
        <Users size={12} /> {title}
      </div>
      <div className="space-y-2">
        {groups.map((group) => (
          <label key={group.id} className="flex items-center gap-3 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={selectedIds.includes(group.id)}
              onChange={() => toggle(group.id)}
              className="rounded border-gray-300 text-navy focus:ring-navy"
            />
            <span>{group.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

interface ShareItemControlProps {
  itemType: 'prayer' | 'grow_goal';
  objectId: number;
}

export function ShareItemControl({ itemType, objectId }: ShareItemControlProps) {
  const { groups, loading } = useMyGroups();
  const [sharedIds, setSharedIds] = useState<number[]>([]);
  const [saving, setSaving] = useState<number | null>(null);

  useEffect(() => {
    accountabilityGroupsAPI
      .getItemShares(itemType, objectId)
      .then(setSharedIds)
      .catch(() => setSharedIds([]));
  }, [itemType, objectId]);

  const toggle = async (groupId: number) => {
    const next = sharedIds.includes(groupId)
      ? sharedIds.filter((id) => id !== groupId)
      : [...sharedIds, groupId];
    setSaving(groupId);
    try {
      const result = await accountabilityGroupsAPI.setItemShares(itemType, objectId, next);
      setSharedIds(result);
    } catch {
      toast.error('Could not update sharing.');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <Loader2 size={14} className="animate-spin" /> Loading groups…
      </div>
    );
  }
  if (groups.length === 0) {
    return (
      <p className="text-xs text-gray-400">
        Join an accountability group to share this.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
        <Users size={12} /> Share with group(s)
      </div>
      <div className="flex flex-wrap gap-2">
        {groups.map((g) => {
          const isShared = sharedIds.includes(g.id);
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => toggle(g.id)}
              disabled={saving === g.id}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                isShared
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {saving === g.id ? (
                <Loader2 size={12} className="animate-spin" />
              ) : isShared ? (
                <Check size={12} />
              ) : null}
              {g.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ShareSnapshotControl() {
  const { groups, setGroups, loading } = useMyGroups();
  const [saving, setSaving] = useState<number | null>(null);

  const toggle = async (groupId: number, enabled: boolean) => {
    setSaving(groupId);
    try {
      await accountabilityGroupsAPI.setShareSnapshot(groupId, enabled);
      setGroups((prev) =>
        prev.map((g) => (g.id === groupId ? { ...g, my_share_snapshot: enabled } : g)),
      );
    } catch {
      toast.error('Could not update snapshot sharing.');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <Loader2 size={14} className="animate-spin" /> Loading groups…
      </div>
    );
  }
  if (groups.length === 0) return null;

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
        <Users size={12} /> Share my latest snapshot
      </div>
      <div className="space-y-1.5">
        {groups.map((g) => (
          <label key={g.id} className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={g.my_share_snapshot}
              disabled={saving === g.id}
              onChange={(e) => toggle(g.id, e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            {g.name}
          </label>
        ))}
      </div>
    </div>
  );
}
